<template>
  <div class="grid-card flex flex-col p-0.5">
    <!-- Card Header -->
    <div class="w-full flex items-center px-2.5 py-1.5">
      <!-- Group By Label -->
      <div class="relative flex-grow flex items-center gap-2 min-w-0">
        <div class="flex-shrink flex items-center min-w-0">
          <BaseTooltipV2 v-if="isCreativeGrouping" alignment="start" class="flex-shrink-0"
          :text="`${totalAdCount} ad${totalAdCount > 1 ? 's' : ''} using this creative`">
            <SameCreativeIcon class="text-icon-normal flex-shrink-0 transition-colors duration-100 hover:text-text-muted" />
          </BaseTooltipV2>
          <BaseTooltipV2 alignment="start" :zIndex="40001">
            <BaseText type="label" size="sm" class="text-text-muted pl-1 truncate cursor-default">
              {{ adLabel }}
            </BaseText>
            <template #tooltip>
              <div class="ad-label-tooltip px-2 py-1.5 flex flex-col gap-1">
                <BaseText type="label" size="xs" class="name text-white">
                  {{ adLabel }}
                </BaseText>
                <BaseText v-if="adLabelTooltipCreativeGroup" type="body" size="xs" class="text-neutral-alpha-800">
                  {{ adLabelTooltipCreativeGroup }}
                </BaseText>
              </div>
            </template>
          </BaseTooltipV2>
        </div>
        <!-- Ad Count Indicator -->
        <BaseText v-if="!isAdLevel" type="label" size="xs" class="px-1.5 py-0.5 bg-neutral-25 text-text-muted" style="border-radius: 4px">
          {{ totalAdCount?.toLocaleString() ?? totalAdCount }}
        </BaseText>
      </div>
      <!-- Group Action Buttons -->
      <div class="ml-auto pl-1 flex items-center" v-if="!isPublic">
        <button v-if="allowCreativeDetails" @click="handleShowDetailsDrawer" class="p-1" @mouseenter="creativeInsightsHovered = true" @mouseleave="creativeInsightsHovered = false">
          <AdDetailsAnimatedIcon :is-hovered="creativeInsightsHovered" />
        </button>
        <button v-if="allowGroupBreakdown" class="p-1" @mouseenter="groupDetailsHovered = true" @mouseleave="groupDetailsHovered = false"
        @click="$emit('showGroupBreakdown')">
          <GroupDetailsIcon :is-hovered="groupDetailsHovered" />
        </button>
      </div>
    </div>
    <div @contextmenu="$emit('handleContextOpen', $event, true)">
      <!-- Group Media Previews -->
      <div class="w-full flex flex-col p-0.5 bg-neutral-25 rounded-lg">
        <!-- Selected Preview Media Viewer -->
        <div class="cell-media-container rounded-md overflow-hidden">
          <!-- Prompt Meta Embed Fallback -->
          <button v-if="promptEmbedFallback && !isPublic" class="group missing-video-banner meta-embed-banner p-2 text-left"
          @click="handleShowDetailsDrawer">
            <div>
              <BaseText type="label" size="xs" class="text-white">Cannot Display Content</BaseText>
              <BaseText class="text-neutral-alpha-650" size="xs">Click to view {{ platformName }} Embed</BaseText>
            </div>
            <div class="external-btn" style="background-color: #3B383D">
              <component :is="platformIcon" class="text-white" />
            </div>
          </button>
          <!-- Missing src url banner -->
          <div v-else-if="isVideoMissing" class="missing-video-banner p-2">
            <div>
              <BaseText type="label" size="xs" class="text-white">Full Asset Unavailable</BaseText>
              <BaseText class="text-neutral-alpha-650" size="xs">
                {{ isPublic ? `Media format not supported` : `Error loading asset from ${platformName}` }}
              </BaseText>
            </div>
            <button v-if="hasExternalVideoUrl" class="external-btn" style="background-color: #3B383D" @click="openExternalSrc">
              <OpenExternalIcon/>
            </button>
          </div>
          <div 
            v-if="getIsPrivateMode" 
            class="backdrop-filter backdrop-blur-sm absolute top-0 left-0 w-full h-full z-50 pointer-events-none" 
          />
          <!-- Blurred Background Image -->
          <div class="blur-image-background" :style="{backgroundImage: `url(${selectedAdPreview.ad_image_url})`}" />
          <!-- Media Element -->
          <BaseAdvertisementVideoPlayer v-if="adPreviewType === 'video' && adPreviewVideoData && !onlyImage"
            :advertisement="adPreviewVideoData"
            contain-media
            class="video-element"
            @handle-video-click="$emit('handleContextClose')"
            @loaded-thumbnail="loadingSelectedAdPreview = false"
            @loaded-content="loadingSelectedAdPreview = false"
          />
          <img v-else
            :src="selectedAdPreview.ad_image_url" 
            class="media-element object-contain" 
            @load="loadingSelectedAdPreview = false" 
          />
          <!-- Loading Spinner -->
          <transition name="loading">
            <div v-if="showLoadingSpinner" class="loading-overlay">
              <div class="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 shadow-lg p-1.5 rounded-lg"
              style="background-color: rgba(0, 0, 0, 0.35); backdrop-filter: blur(4px); opacity: 0.75">
                <BaseLoadingSpinner />
              </div>
            </div>
          </transition>
        </div>
        <!-- Preview Selector -->
        <div v-if="!isAdLevel" ref="previewSelector" class="w-full flex items-center gap-1.5 px-1.5 pt-1.5 pb-1">
          <!-- Thumbnail Buttons -->
          <AssetPreviewItem 
            v-for="(adPreview, index) in visibleAdPreviews" :key="`ad-preview-${index}`"
            class="flex-shrink-0"
            :asset="adPreview"
            :size="adPreviewSize"
            :isSelected="selectedPreviewIndex === index"
            :doAnimate="doAnimatePreviewSelection"
            @selected="() => {changePreviewSelection(index)}"
          />
          <!-- Hidden Preview Counter -->
          <div v-if="visibleAdPreviews.length < totalAdCount" class="ml-auto">
            <BaseText type="label" size="xs" class="text-text-muted p-0.5">
              +{{ totalAdCount - visibleAdPreviews.length }}
            </BaseText>
          </div>
        </div>
      </div>
      <!-- Group Breakdown Info -->
      <div class="w-full flex flex-col gap-1 px-1 py-1.5">
        <!-- Group Media Type -->
        <div class="w-full flex items-center justify-between px-1.5">
          <BaseText type="body" size="sm" class="text-text-normal">
            Type
          </BaseText>
          <div class="flex items-center gap-1 px-1.5 py-1">
            <component :is="groupMediaType.icon" :width="18" :height="18" stroke="#5E6678" />
            <BaseText type="label" size="sm" class="text-text-muted">
              {{ groupMediaType.name }}
            </BaseText>
          </div>
        </div>
        <!-- Selected Metric Values -->
        <transition-group :name="kpiGroupTransition" type="transition" class="flex flex-col gap-1">
          <div v-for="kpi in selectedKpis" :key="`${data.group_by || data.ad_id}-metric-${kpi}`" 
            class="w-full flex items-center justify-between gap-2 px-1.5 min-w-0">
            <BaseText type="body" size="sm" class="flex-shrink text-text-normal truncate">
              {{ getMetricName(kpi) }}
            </BaseText>
            <div v-if="getMetricType(kpi) === 'percentile'" class="flex-grow flex-shrink-0 ml-auto">
              <PercentileMetricScale :value="data?.[kpi]" :maxBarWidth="68" useLabelWeight class="justify-end" />
            </div>
            <BaseText v-else type="label" size="sm" class="px-1.5 py-0.5 rounded-md flex-shrink-0"
            :style="{backgroundColor: getMetricScoreColor(kpi).background, color: getMetricScoreColor(kpi).text}">
              {{ getIsPrivateMode ? '••••' : formatMetricValueString(data?.[kpi], kpi) }}
            </BaseText>
          </div>
        </transition-group>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import formatLabelString from '../../../../utils/lens/formatGraphLabelString'
import { platformNameMap, platformIconMap } from '@/utils/lens/dataSourceUtils'
import { getRelativeMetricScoreColor } from '../../../../utils/lens/metricScoreUtil'
import AssetPreviewItem from './AssetPreviewItem.vue'
import PercentileMetricScale from '../../PercentileMetricScale.vue'

// Icons
import AdDetailsAnimatedIcon from '../../../globals/Icons/AdDetailsAnimatedIcon.vue'
import GroupDetailsIcon from '../../../globals/Icons/LensIcons/GroupDetailsAnimatedIcon.vue'
import FormatIcon from '../../../globals/Icons/FilterIcons/FormatIcon.vue'
import ImageFormatIcon from '../../../globals/Icons/FilterIcons/ImageFormatIcon.vue'
import VideoFormatIcon from '../../../globals/Icons/FilterIcons/VideoFormatIcon.vue'
import OpenExternalIcon from '../../../globals/Icons/OpenExternalIcon.vue'
import MetaIcon from '../../../globals/Icons/LensIcons/MetaMetricsIcon.vue'
import SameCreativeIcon from '../../../globals/Icons/SameCreativeIcon.vue'

export default {
  name: 'GridGraphCard',
  components: {
    AssetPreviewItem,
    PercentileMetricScale,
    AdDetailsAnimatedIcon,
    OpenExternalIcon,
    GroupDetailsIcon,
    MetaIcon,
    SameCreativeIcon
  },
  props: {
    data: {
      type: Object,
      default: () => {}
    },
    dataSummary: {
      type: Object,
      default: () => {}
    },
    selectedKpis: {
      type: Array,
      default: () => []
    },
    isAdLevel: {
      type: Boolean,
      default: false
    },
    isPublic: {
      type: Boolean,
      default: false
    },
    onlyImage: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      selectedPreviewIndex: 0,
      visibleAdPreviews: [],

      // UI State
      creativeInsightsHovered: false,
      groupDetailsHovered: false,
      doAnimatePreviewSelection: false,
      adPreviewSize: 32, // px - Maybe make this sizing customizable?
      resizeObserver: null,
      loadingSelectedAdPreview: true,
      loaderThresholdCrossed: false,
      previousKpis: [],
    }
  },
  watch: {
    selectedAdPreview(newVal, oldVal) {
      if (newVal.ad_image_url === oldVal.ad_image_url) return
      this.loadingSelectedAdPreview = true
    },
    selectedKpis (newVal) {
      // Acts as a mechanism for determining if the selected KPIs have been reordered (as opposed to insert or remove)
      setTimeout(() => { this.previousKpis = [...newVal] }, 10)
    }
  },
  computed: {
    ...mapGetters('MetricsModule', ['getMetricsPlatform', 'getMetricLookup']),
    ...mapGetters('LensModule', ['getColorFormat']),
    ...mapGetters('MiscModule', ['getIsPrivateMode']),
    adLabel () {
      return this.getIsPrivateMode 
        ? '•••••••••••••••••••' 
        : this.isCreativeGrouping
          ? this.data?.ads?.[0]?.ad_name
          : (this.data.group_by || this.data.ad_name)
    },
    adLabelTooltipCreativeGroup () {
      if (!this.isCreativeGrouping) return null
      const uniqueCount = this.data?.group_labels?.length ?? 0
      if (uniqueCount < 2) return null
      return `+${uniqueCount - 1} additional ad ${uniqueCount > 2 ? 'names' : 'name'}`
    },
    isCreativeGrouping () {
      // We have the startsWith fallback because switching off creative grouping has a delay before grid is updated
      return !this.isAdLevel 
        && (this.dataSummary?.groupedBy === 'creative' || this.data?.group_by?.startsWith('https://lens.assets'))
    },
    selectedAdPreview () {
      if (this.isAdLevel) return this.data
      return this.data?.ads?.[this.selectedPreviewIndex]
    },
    adPreviewType () {
      return this.selectedAdPreview.ad_type;
      // if (this.selectedAdPreview.video_url_source) return 'video'
      // return 'image'
    },
    adPreviewVideoData () {
      const { ad_id, ad_image_url, video_url_source } = this.selectedAdPreview
      if (!ad_id || !video_url_source) return null
      return {
        id: ad_id,
        ...(ad_image_url ? { thumbnail: ad_image_url } : {}),
        video: video_url_source
      }
    },
    isVideoMissing () {
      if (this.isAdLevel) {
        return this.data.ad_type === 'video' && !this.data.video_url_source;
      }
      return this.selectedAdPreview.ad_type === 'video' && !this.selectedAdPreview.video_url_source;
    },
    promptEmbedFallback () {
      return !!(
        this.isVideoMissing
        && this.selectedAdPreview.iframe
        && (this.getMetricsPlatform !== 'meta' || (this.selectedAdPreview.width && this.selectedAdPreview.height))
      )
    },
    groupMediaType () {
      if (this.isAdLevel) {
        if (this.data.ad_type === 'video') return { name: 'Video', icon: VideoFormatIcon }
        return { name: 'Image', icon: ImageFormatIcon }
      }
      switch (this.data.group_ads_type) {
        case 'video': return { name: 'Video', icon: VideoFormatIcon }
        case 'image': return { name: 'Image', icon: ImageFormatIcon }
        default: return { name: 'Multiple', icon: FormatIcon }
      }
    },
    showLoadingSpinner () {
      return this.loaderThresholdCrossed && this.loadingSelectedAdPreview
    },
    allowCreativeDetails () {
      return this.dataSummary?.reportType !== 'comparative' || this.isAdLevel
    },
    allowGroupBreakdown () {
      const groupedBy = this.dataSummary?.groupedBy
      return !this.isAdLevel && (this.data?.ads?.[0]?.[groupedBy] || groupedBy === 'creative')
    },
    kpiGroupTransition () {
      // Only return the transition name if the arrays have the same length (indicating a reorder)
      return this.previousKpis.length === this.selectedKpis.length ? 'flip-list' : 'none'
    },
    totalAdCount () {
      if (this.isAdLevel) return 0
      return this.data.active_ad_count + this.data.paused_ad_count
    },
    platformName () {
      return platformNameMap[this.getMetricsPlatform]
    },
    platformIcon () {
      return platformIconMap[`mono-${this.getMetricsPlatform}`]
    },
    hasExternalVideoUrl () {
      if (this.isAdLevel) {
        return !!this.data.video_url
      }
      return !!this.selectedAdPreview.video_url
    }
  },
  created () {
    this.previousKpis = [...this.selectedKpis]
  },
  mounted () {
    this.$nextTick(() => {
      if (!this.isAdLevel) {
        this.computeVisibleAdPreviews()
        // Initialize a resize observer to watch the preview selector
        this.resizeObserver = new ResizeObserver(() => {
          this.computeVisibleAdPreviews()
        })
        this.resizeObserver.observe(this.$refs.previewSelector)
      }

      // Set a timer to display the loading spinner after a threshold of time has passed
      setTimeout(() => { this.loaderThresholdCrossed = true }, 300)
    })
  },
  beforeDestroy () {
    if (this.resizeObserver && this.$refs.previewSelector) {
      this.resizeObserver.unobserve(this.$refs.previewSelector)
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  },
  methods: {
    handleShowDetailsDrawer () {
      if (this.selectedPreviewIndex > 0) this.$emit('showDetailsDrawerAtIndex', this.selectedPreviewIndex)
      else this.$emit('showDetailsDrawer')
    },
    openExternalSrc () {
      if (this.isAdLevel) {
        window.open(this.data.video_url, '_blank')
      } else {
        window.open(this.selectedAdPreview.video_url, '_blank')
      }
    },
    changePreviewSelection (index) {
      if (!this.doAnimatePreviewSelection) this.doAnimatePreviewSelection = true
      this.selectedPreviewIndex = index
    },
    getMetricName (kpi) {
      return this.getMetricLookup?.[kpi]?.name || kpi
    },
    getMetricType (kpi) {
      return this.getMetricLookup?.[kpi]?.type
    },
    formatMetricValueString (value, kpi) {
      const type = this.getMetricType(kpi)
      return formatLabelString(value, type, this.dataSummary?.currency)
    },
    computeVisibleAdPreviews () {
      if (this.isAdLevel || !this.data?.ads || !this.data.ads.length || !this.$refs.previewSelector) return

      const LIST_GAP = 6 // px
      const computeListWidth = (numAds) => {
        return numAds * this.adPreviewSize + (numAds - 1) * LIST_GAP
      }

      // Compute the number of visible previews
      const availableWidth = this.$refs.previewSelector.getBoundingClientRect().width - 12 // -12px for padding
      const numAds = this.totalAdCount
      const previewListWidth = computeListWidth(numAds)
      if (previewListWidth <= availableWidth) {
        this.visibleAdPreviews = this.data.ads.map(ad => ad.ad_image_url)
      } else {
        let numVisiblePreviews = Math.min(Math.floor((availableWidth - LIST_GAP) / (this.adPreviewSize + LIST_GAP)), 8)

        // Compute the rendered size of the hidden preview counter and adjust the number of visible previews if necessary
        const compNode = document.createElement('span')
        compNode.classList.add('p-0.5', 'text-label-xs', 'opacity-0', 'pointer-events-none')
        compNode.textContent = `+${numAds - numVisiblePreviews}`
        document.body.appendChild(compNode)
        const counterWidth = compNode.getBoundingClientRect().width + LIST_GAP
        document.body.removeChild(compNode)
        if (computeListWidth(numVisiblePreviews) + counterWidth > availableWidth) numVisiblePreviews--
        
        this.visibleAdPreviews = this.data.ads.slice(0, numVisiblePreviews).map(ad => ad.ad_image_url)
      }
    },
    getMetricScoreColor (kpi) {
      const NEUTRAL_COLOR = { background: '#F6F8FA', text: '#303546' }
      const EXCLUDED_KPIS = ['spend']
      if (EXCLUDED_KPIS.includes(kpi) || !this.dataSummary?.ad_groups) return NEUTRAL_COLOR

      const percentDiff = Math.floor(this.data.percent_deviation?.[kpi])
      const metricDir = this.getMetricLookup?.[kpi]?.metric_direction
      const colorInfo = getRelativeMetricScoreColor(percentDiff, kpi, this.getColorFormat, this.dataSummary, metricDir)
      if (!colorInfo.scoreColor.length) return NEUTRAL_COLOR
      
      const textColor = colorInfo.direction === 'positive' ? '#005043' : colorInfo.direction === 'negative' ? '#7C001F' : '#303546'
      return { background: colorInfo.scoreColor, text: textColor }
    }
  }
}
</script>

<style scoped>
.grid-card {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  background-color: white;
  box-shadow: 0px 1.5px 1.5px -0.75px rgba(6, 7, 16, 0.08), 0px 0px 0.25px 0.75px rgba(6, 7, 16, 0.04);
}
.ad-label-tooltip {
  border-radius: 8px;
  background: rgba(6, 7, 16, 0.88);
  backdrop-filter: blur(6px);
  width: max-content;
  max-width: 290px;
  overflow: hidden;
  word-break: break-all;
  z-index: 40001;
}
.ad-label-tooltip .name {
  display: -webkit-box;
  line-clamp: 3;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cell-media-container {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
}
.blur-image-background {
  position: relative;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  backdrop-filter: blur(0px);
}
.blur-image-background::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(235, 239, 243, 0.25);
  backdrop-filter: blur(12px);
}
.media-element {
  position: absolute;
  z-index: 10;
  top: 0;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  height: 100%;
}
.video-element {
  position: absolute;
  z-index: 10;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.label-hover-tooltip {
  position: absolute;
  bottom: calc(100% + 4px);
  left: -4px;
  max-width: 200px;
  z-index: 40001; /* So it's above the filter toolbar */
}
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(19, 21, 32, 0.1);
  z-index: 50;
}

.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}

.loading-enter-active, .loading-leave-active {
  transition: opacity 100ms ease-in-out;
}
.loading-enter-from, .loading-enter, .loading-leave-to {
  opacity: 0;
}
.loading-enter-to, .loading-leave-from {
  opacity: 1;
}

.flip-list-move {
  transition: transform 200ms ease-in-out;
}

.missing-video-banner{
  border-radius: 6px;
  z-index: 50;
  width: calc(100% - 10px);
  background-color: rgba(6, 7, 16, 0.88);
  display: flex;
  align-items: center;
  justify-content: space-between;

  position: absolute;
  left:5px;
  bottom:5px;
}
.missing-video-banner.meta-embed-banner {
  transition: background-color 100ms ease-in-out;
}
.missing-video-banner.meta-embed-banner:hover {
  background-color: rgba(6, 7, 16, 0.95);
}
.external-btn{
  border-radius: 6px;
  padding: 6px;
  color: white;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 100%), rgba(255, 255, 255, 0.12);
  background: linear-gradient(180deg, color(display-p3 1 1 1 / 0.00) 0%, color(display-p3 1 1 1 / 0.08) 100%), color(display-p3 1 1 1 / 0.12);

  /* Button/Secondary/Normal */
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  box-shadow: 0px 1px 2px 0px color(display-p3 0.0368 0.0984 0.2832 / 0.13), 0px 0px 0px 1px color(display-p3 0.0696 0.2157 0.4104 / 0.08);
}
</style>
